"use client";

import { ArrowRight, BookOpen, Palette, Leaf, Users, BrainCircuit, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Curriculum() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const services = [{
    spec: "[AREA-01 / PRACTICAL]",
    title: "Practical Life",
    description: "Developing independence through real-world tasks like pouring, cleaning, and dressing, fostering confidence and coordination.",
    Icon: Sparkles
  }, {
    spec: "[AREA-02 / SENSORIAL]",
    title: "Sensorial",
    description: "Refining the senses through tactile materials that help children categorize and understand the physical world around them.",
    Icon: Palette
  }, {
    spec: "[AREA-03 / LANGUAGE]",
    title: "Language",
    description: "Building a rich vocabulary and early literacy skills through phonetic exploration, storytelling, and expressive communication.",
    Icon: BookOpen
  }, {
    spec: "[AREA-04 / MATHEMATICS]",
    title: "Mathematics",
    description: "Concrete exploration of numbers and quantities, moving from physical manipulatives to abstract mathematical concepts.",
    Icon: BrainCircuit
  }, {
    spec: "[AREA-05 / CULTURE]",
    title: "Culture & Science",
    description: "Exploring geography, biology, and the natural world to foster a sense of wonder and global citizenship.",
    Icon: Leaf
  }, {
    spec: "[AREA-06 / SOCIAL]",
    title: "Social Grace",
    description: "Learning empathy, conflict resolution, and community manners in a respectful, collaborative classroom environment.",
    Icon: Users
  }];
  return <section data-section-id="1791" id="curriculum-areas" className="relative bg-background py-28 md:py-40 overflow-hidden">
      {}
      <div aria-hidden="true" className="absolute top-0 left-0 right-0 h-6 bg-primary" style={{
      backgroundImage: "repeating-linear-gradient(45deg, hsl(145 30% 12%) 0, hsl(145 30% 12%) 4px, hsl(145 30% 8%) 4px, hsl(145 30% 8%) 8px)"
    }} />

      <div className="max-w-[1360px] mx-auto px-6 md:px-8 lg:px-16">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 mb-16 md:mb-24 transition-all duration-500 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="lg:col-span-8">
            {}
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-block w-2.5 h-2.5 bg-secondary" />
              <span className="font-mono text-xs md:text-sm font-semibold tracking-[0.18em] uppercase text-foreground">
                § 02 — Our Curriculum
              </span>
            </div>

            <h2 className="font-default font-semibold text-foreground leading-[1.1] tracking-tight text-5xl md:text-6xl lg:text-7xl xl:text-[5rem]">
              The five pillars of <em className="not-italic font-serif text-secondary-foreground">growth.</em>
            </h2>
          </div>

          <div className="lg:col-span-4 flex flex-col justify-end">
            <p className="font-default text-base leading-relaxed text-muted-foreground mb-6">
              Our Montessori program is built on five core areas of development. We provide a prepared environment where children follow their natural curiosity to build a foundation for lifelong learning.
            </p>
            <Link to="/OurProgramPage" className="group inline-flex items-center gap-2 font-mono text-sm font-semibold uppercase tracking-wider text-foreground hover:text-secondary-foreground transition-colors duration-200">
              <span>View full program details</span>
              <ArrowRight className="w-4 h-4 text-secondary-foreground transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        {}
        <div aria-hidden="true" className="h-1.5 w-32 mb-0" style={{
        backgroundImage: "repeating-linear-gradient(45deg, hsl(45 90% 45%) 0, hsl(45 90% 45%) 12px, hsl(145 30% 12%) 12px, hsl(145 30% 12%) 24px)"
      }} />

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t-2 border-l-2 border-border">
          {services.map((service, i) => {
          const Icon = service.Icon;
          return <article key={i} data-index={i} className="group relative bg-card border-r-2 border-b-2 border-border p-8 min-h-[280px] flex flex-col transition-colors duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)] hover:bg-primary cursor-pointer">
                {}
                <div className="flex items-start justify-between mb-auto">
                  <Icon className="w-9 h-9 stroke-[1.5] text-foreground transition-colors duration-200 group-hover:text-accent" strokeWidth={1.5} />
                  <span className="font-mono text-xs font-medium tracking-[0.12em] uppercase text-foreground transition-colors duration-300 group-hover:text-accent">
                    {service.spec}
                  </span>
                </div>

                {}
                <div className="relative inline-block mt-8 mb-3">
                  <h3 className="font-default font-semibold uppercase text-2xl md:text-[1.75rem] leading-none tracking-tight text-foreground transition-colors duration-300 group-hover:text-primary-foreground">
                    {service.title}
                  </h3>
                  <span aria-hidden="true" className="absolute left-0 -bottom-2 h-[3px] w-0 bg-secondary transition-all duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover:w-full" />
                </div>

                {}
                <p className="font-default text-base leading-relaxed text-foreground/85 transition-colors duration-300 group-hover:text-primary-foreground/85 mt-4">
                  {service.description}
                </p>

                {}
                <div className="mt-6 flex justify-end">
                  <span className="inline-flex items-center gap-1.5 font-mono text-xs font-medium uppercase tracking-wider text-foreground transition-colors duration-300 group-hover:text-accent">
                    <ArrowRight className="w-3.5 h-3.5" />
                    Learn More
                  </span>
                </div>
              </article>;
        })}
        </div>
      </div>
    </section>;
}